import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MigratedRevenueSplitResponse,
  MigratedSplitAmount,
  SplitAmount,
} from "../../api/services/program/models";
import {
  useGetMigratedRevenueSplitForProjectByInvoiceId,
  useGetRevenueSplitForProgramInvoiceByInvoiceId,
} from "../../api/services/program/programApis";
import { BRACKETS, SYMBOL } from "../../helpers/constants";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import {
  getAmountWithCurrency,
  getAmountWithFormatByCurrencyName,
  getCurrencyFormat,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
} from "../../helpers/utils";
import { useGridContainer } from "../../hooks/useGridContainer";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { manageRevenueSplitSiteCoreKeys } from "../../pages/createProgram/siteCore/SiteCoreKeys";
import { siteCoreContentLoadingSelector } from "../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { AccordionHeader } from "../accordionHeader/AccordionHeader";
import { AccordionHeaderText } from "../accordionHeader/AccordionHeaderText";
import { MuiBox } from "../box/MuiBox";
import { MuiDoubleLabel } from "../doubleLabel/MuiDoubleLabel";
import { MuiGrid } from "../grid/MuiGrid";
import { MuiModal } from "../modal/MuiModal";
import { CURRENCY_FORMAT_MAP } from "../textFieldCurrency/CurrencyUtils";
import { MuiTripleLabel } from "../tripleLabel/MuiTripleLabel";
import { MuiTypography } from "../typography/MuiTypography";
import { ManageRevenueSplitWrapper } from "./ManageRevenueSplit.style";
import ManageRevenueSplitTable, {
  ManageRevenueSplitRow,
} from "./manageRevenueSplitTable/ManageRevenueSplitTable";

interface ManageRevenueSplitProps {
  invoiceId: string;
  invoiceNumber?: string;
  isViewOnly?: boolean;
  isMigratedRevenueSplits?: boolean;
  invoiceStatus?: string;
  isInvoiceReversed?: boolean;
  isOpen: boolean;
  onClose: () => void;
  isForProject: boolean;
}

export const ManageRevenueSplit: React.FC<ManageRevenueSplitProps> = ({
  invoiceNumber = "",
  invoiceId,
  isViewOnly,
  isMigratedRevenueSplits,
  invoiceStatus,
  isInvoiceReversed,
  isOpen,
  onClose,
  isForProject,
}) => {
  const {
    fetchLabel,
    fetchComponentTitle,
    commonLabels,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { isMobile } = useMediaQueries();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { gridSizeMedium: gridSize } = useGridContainer(true);
  const customGridSize = { lg: 3, md: 4, xs: 12, sm: 6 };
  const [sectionItems, setSectionItems] = useState<
    {
      label: keyof typeof manageRevenueSplitSiteCoreKeys;
      value: string;
      subValue: string;
    }[]
  >([]);
  const [revenueSplitAmountData, setRevenueSplitAmountData] = useState<
    ManageRevenueSplitRow[]
  >([]);
  const [currencyLocale, setCurrencyLocale] = useState(
    CURRENCY_FORMAT_MAP.USD.locale,
  );

  const getLabel = (
    key: keyof typeof manageRevenueSplitSiteCoreKeys,
  ): string => {
    return fetchLabel(
      manageRevenueSplitSiteCoreKeys.manageRevenueSplit,
      manageRevenueSplitSiteCoreKeys[key],
    );
  };

  const {
    data: revenueSplitData,
    isPending: isRevenueSplitPending,
    isError: isRevenueSplitError,
    error: revenueSplitError,
    mutateAsync: fetchRevenueSplitData,
  } = useGetRevenueSplitForProgramInvoiceByInvoiceId();

  const {
    data: migratedRevenueSplitData,
    isPending: isMigratedRevenueSplitPending,
    isError: isMigratedRevenueSplitError,
    error: migratedRevenueSplitError,
    mutateAsync: fetchMigratedRevenueSplitData,
  } = useGetMigratedRevenueSplitForProjectByInvoiceId();

  useEffect(() => {
    if (invoiceId) {
      handleFetchRevenueSplitData(invoiceId);
    }
  }, [invoiceId, isMigratedRevenueSplits, isForProject]);

  const handleFetchRevenueSplitData = (invoiceId: string) => {
    if (isMigratedRevenueSplits) {
      fetchMigratedRevenueSplitData(invoiceId);
    } else {
      fetchRevenueSplitData({ isForProject, invoiceId });
    }
  };

  useEffect(() => {
    const isPending = isMigratedRevenueSplits
      ? isMigratedRevenueSplitPending
      : isRevenueSplitPending;
    dispatch(
      toggleLoader({
        ManageRevenueSplitLoading: {
          isVisible: isPending,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          ManageRevenueSplitLoading: {
            isVisible: false,
          },
        }),
      );
    };
  }, [
    isRevenueSplitPending,
    isMigratedRevenueSplitPending,
    isMigratedRevenueSplits,
  ]);

  useEffect(() => {
    if (isRevenueSplitError && isNotEmpty(revenueSplitError)) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isRevenueSplitError]);

  useEffect(() => {
    if (isMigratedRevenueSplitError && isNotEmpty(migratedRevenueSplitError)) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isMigratedRevenueSplitError]);

  // Use migrated data when isMigratedRevenueSplits is true
  const currentRevenueSplitData = useMemo(() => {
    return isMigratedRevenueSplits
      ? migratedRevenueSplitData
      : revenueSplitData;
  }, [isMigratedRevenueSplits, migratedRevenueSplitData, revenueSplitData]);

  const getFormattedCurrency = (
    amount?: number,
    currency: string = currencyName ?? "",
  ) => {
    return getAmountWithCurrency(amount ?? 0, currency);
  };

  const currencyName = useMemo(
    () =>
      isForProject
        ? currentRevenueSplitData?.projectFeeCurrency
        : currentRevenueSplitData?.programFeeCurrency,
    [
      currentRevenueSplitData?.programFeeCurrency,
      isForProject,
      currentRevenueSplitData?.projectFeeCurrency,
    ],
  );

  const programProjectFeeLabel = useMemo(() => {
    return isForProject ? getLabel("projectFee") : getLabel("programFee");
  }, [isForProject]);

  const programProjectFeeFormattedValue = useMemo(() => {
    return getFormattedCurrency(
      isForProject
        ? currentRevenueSplitData?.projectFee
        : currentRevenueSplitData?.programFee,
    );
  }, [
    isForProject,
    currentRevenueSplitData?.programFee,
    currentRevenueSplitData?.projectFee,
  ]);

  useEffect(() => {
    if (isNotEmpty(currencyName)) {
      const currencyFormat = getCurrencyFormat(currencyName);
      setCurrencyLocale(currencyFormat?.locale);
    }
  }, [currencyName]);

  const getFormattedAmountAndPercentage = ({
    amount,
    percentage,
    isTableView = true,
  }: {
    amount?: number;
    percentage?: number;
    isTableView?: boolean;
  }) => {
    const formattedAmount = getFormattedCurrency(amount);
    if (isMigratedRevenueSplits) {
      return formattedAmount;
    }

    const formattedPercentage = isNotEmpty(percentage)
      ? `${percentage}${SYMBOL.PERCENTAGE}`
      : "";
    if (!isTableView && isNotEmpty(percentage)) {
      return `${BRACKETS.LEFT_SQUARE}${formattedPercentage} ${commonLabels.of} ${programProjectFeeFormattedValue}${BRACKETS.RIGHT_SQUARE}`;
    }
    if (isNotEmpty(percentage)) {
      return `${formattedPercentage} ${BRACKETS.LEFT_SQUARE}${formattedAmount}${BRACKETS.RIGHT_SQUARE}`;
    }
    return formattedAmount;
  };

  const splitAmounts = useMemo(
    () => currentRevenueSplitData?.revenueSplitBreakdownResponse?.splitAmounts,
    [currentRevenueSplitData?.revenueSplitBreakdownResponse?.splitAmounts],
  );
  const splitTotals = useMemo(
    () => currentRevenueSplitData?.revenueSplitBreakdownResponse?.splitTotals,
    [currentRevenueSplitData?.revenueSplitBreakdownResponse?.splitTotals],
  );

  useEffect(() => {
    if (!siteCoreLoading && isNotEmpty(currentRevenueSplitData)) {
      if (isNotEmpty(splitAmounts) && isNotEmpty(splitTotals)) {
        const totalLabel = getLabel("revenueSplitSectionTotalTableColumn");

        const mappedSplitAmounts: ManageRevenueSplitRow[] = splitAmounts.map(
          (splitAmount: SplitAmount | MigratedSplitAmount) => {
            const officeId =
              "id" in splitAmount.office ? splitAmount.office.id : "";

            return {
              id: splitAmount?.order,
              officeAffiliate:
                splitAmount?.order === 1 && !isMigratedRevenueSplits
                  ? fetchLabel(
                      manageRevenueSplitSiteCoreKeys.manageRevenueSplit,
                      splitAmount.office.name,
                    )
                  : getValueOrBlank(splitAmount.office.name),
              officeId: officeId,
              sales: getFormattedAmountAndPercentage(splitAmount.sales),
              counseling: getFormattedAmountAndPercentage(
                splitAmount.counseling,
              ),
              counselingAmountValue: getAmountWithFormatByCurrencyName(
                splitAmount.counseling.amount,
                getValueOrBlank(currencyName),
              ),
              affiliateRoyalty: getFormattedAmountAndPercentage(
                splitAmount.affiliateRoyalty,
              ),
              royaltyRetention: getFormattedAmountAndPercentage(
                splitAmount.royaltyRetention,
              ),
              total: getFormattedAmountAndPercentage(splitAmount.total),
              relationShipType:
                currentRevenueSplitData?.relationshipTypeDescription,
            };
          },
        );

        const mappedSplitTotal: ManageRevenueSplitRow = {
          id: splitTotals?.order,
          officeAffiliate: totalLabel,
          sales: getFormattedAmountAndPercentage(splitTotals.sales),
          counseling: getFormattedAmountAndPercentage(splitTotals.counseling),
          counselingAmountValue: getAmountWithFormatByCurrencyName(
            splitTotals.counseling.amount,
            getValueOrBlank(currencyName),
          ),
          affiliateRoyalty: getFormattedAmountAndPercentage(
            splitTotals.affiliateRoyalty,
          ),
          royaltyRetention: getFormattedAmountAndPercentage(
            splitTotals.royaltyRetention,
          ),
          total: getFormattedAmountAndPercentage(splitTotals.total),
          relationShipType:
            currentRevenueSplitData?.relationshipTypeDescription,
        };
        mappedSplitAmounts.push(mappedSplitTotal);
        setRevenueSplitAmountData(mappedSplitAmounts);
      }
    }
  }, [
    currentRevenueSplitData,
    siteCoreLoading,
    splitAmounts,
    splitTotals,
    isMigratedRevenueSplits,
  ]);

  useEffect(() => {
    if (splitTotals) {
      setSectionItems([
        {
          label: "revenueSplitSectionSales",
          value: getFormattedCurrency(splitTotals?.sales?.amount),
          subValue: !isMigratedRevenueSplits
            ? getFormattedAmountAndPercentage({
                ...splitTotals?.sales,
                isTableView: false,
              })
            : "",
        },
        {
          label: "revenueSplitSectionCounseling",
          value: getFormattedCurrency(splitTotals?.counseling?.amount),
          subValue: !isMigratedRevenueSplits
            ? getFormattedAmountAndPercentage({
                ...splitTotals?.counseling,
                isTableView: false,
              })
            : "",
        },
        {
          label: "revenueSplitSectionAffiliateRoyalty",
          value: getFormattedCurrency(splitTotals?.affiliateRoyalty?.amount),
          subValue: !isMigratedRevenueSplits
            ? getFormattedAmountAndPercentage({
                ...splitTotals?.affiliateRoyalty,
                isTableView: false,
              })
            : "",
        },
        {
          label: "revenueSplitSectionRoyaltyRetention",
          value: getFormattedCurrency(splitTotals?.royaltyRetention?.amount),
          subValue: !isMigratedRevenueSplits
            ? getFormattedAmountAndPercentage({
                ...splitTotals?.royaltyRetention,
                isTableView: false,
              })
            : "",
        },
      ]);
    }
  }, [currentRevenueSplitData, splitTotals, isMigratedRevenueSplits]);

  const getTitle = () => {
    if (isMigratedRevenueSplits) {
      return replaceHashWithValue(
        getLabel("viewRevenueSplitMigratedTitle"),
        invoiceNumber,
      );
    }

    const title = isViewOnly
      ? getLabel("viewRevenueSplitTitle")
      : fetchComponentTitle(manageRevenueSplitSiteCoreKeys.manageRevenueSplit);

    return replaceHashWithValue(title, invoiceNumber);
  };

  return (
    <MuiModal
      id={COMPONENT_IDS.REVENUE_SPLIT_MODAL}
      open={isOpen}
      onClose={onClose}
      title={getTitle()}
      secondaryButtonTitle={commonLabels.back}
      onSecondaryButtonClick={onClose}
      hasLargeContent
    >
      <ManageRevenueSplitWrapper>
        <MuiBox className={"manage-revenue-split-modal-box"}>
          {isMigratedRevenueSplits ? (
            <>
              {/* Migrated Layout: Row 1 - Relationship Type | Project Key | Project Name */}
              <MuiGrid
                container
                columnSpacing={2}
                rowSpacing={isMobile ? 2 : 5}
              >
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-relationshipType"
                >
                  <MuiDoubleLabel
                    label={getLabel("relationshipType")}
                    replaceEmptyWithDash
                    value={fetchValueByKeyOfDropdown(
                      manageRevenueSplitSiteCoreKeys.manageRevenueSplit,
                      manageRevenueSplitSiteCoreKeys.relationshipTypeList,
                      getValueOrBlank(
                        currentRevenueSplitData?.relationshipTypeDescription,
                      ),
                    )}
                  />
                </MuiGrid>
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-projectKey"
                >
                  <MuiDoubleLabel
                    label={getLabel("projectKey")}
                    replaceEmptyWithDash
                    value={getValueOrBlank(
                      (currentRevenueSplitData as MigratedRevenueSplitResponse)
                        ?.projectDetails?.projectKey,
                    )}
                  />
                </MuiGrid>
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-projectName"
                >
                  <MuiDoubleLabel
                    label={getLabel("projectName")}
                    replaceEmptyWithDash
                    value={getValueOrBlank(
                      (currentRevenueSplitData as MigratedRevenueSplitResponse)
                        ?.projectDetails?.projectName,
                    )}
                  />
                </MuiGrid>
              </MuiGrid>
              {/* Migrated Layout: Row 2 - Accounting Period | Sales Office | Delivery Office */}
              <MuiGrid
                container
                columnSpacing={2}
                rowSpacing={isMobile ? 2 : 5}
              >
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-accountingPeriod"
                >
                  <MuiDoubleLabel
                    label={getLabel("accountingPeriod")}
                    replaceEmptyWithDash
                    value={getValueOrBlank(
                      currentRevenueSplitData?.accountingPeriod?.name,
                    )}
                  />
                </MuiGrid>
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-salesOffice"
                >
                  <MuiDoubleLabel
                    label={getLabel("salesOffice")}
                    replaceEmptyWithDash
                    value={getValueOrBlank(
                      currentRevenueSplitData?.salesOffice?.name,
                    )}
                  />
                </MuiGrid>
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-deliveryOffice"
                >
                  <MuiDoubleLabel
                    label={getLabel("deliveryOffice")}
                    replaceEmptyWithDash
                    value={getValueOrBlank(
                      currentRevenueSplitData?.deliveryOffice?.name,
                    )}
                  />
                </MuiGrid>
              </MuiGrid>
            </>
          ) : (
            <>
              {/* Non-Migrated Layout: Row 1 - Relationship Type | Accounting Period */}
              <MuiGrid
                container
                columnSpacing={2}
                rowSpacing={isMobile ? 2 : 5}
              >
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-relationshipType"
                >
                  <MuiDoubleLabel
                    label={getLabel("relationshipType")}
                    replaceEmptyWithDash
                    value={fetchValueByKeyOfDropdown(
                      manageRevenueSplitSiteCoreKeys.manageRevenueSplit,
                      manageRevenueSplitSiteCoreKeys.relationshipTypeList,
                      getValueOrBlank(
                        currentRevenueSplitData?.relationshipTypeDescription,
                      ),
                    )}
                  />
                </MuiGrid>
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-accountingPeriod"
                >
                  <MuiDoubleLabel
                    label={getLabel("accountingPeriod")}
                    replaceEmptyWithDash
                    value={getValueOrBlank(
                      currentRevenueSplitData?.accountingPeriod?.name,
                    )}
                  />
                </MuiGrid>
              </MuiGrid>
              {/* Non-Migrated Layout: Row 2 - Sales Office | Delivery Office */}
              <MuiGrid
                container
                columnSpacing={2}
                rowSpacing={isMobile ? 2 : 5}
              >
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-salesOffice"
                >
                  <MuiDoubleLabel
                    label={getLabel("salesOffice")}
                    replaceEmptyWithDash
                    value={getValueOrBlank(
                      currentRevenueSplitData?.salesOffice?.name,
                    )}
                  />
                </MuiGrid>
                <MuiGrid
                  size={gridSize}
                  className="manage-revenue-split-deliveryOffice"
                >
                  <MuiDoubleLabel
                    label={getLabel("deliveryOffice")}
                    replaceEmptyWithDash
                    value={getValueOrBlank(
                      currentRevenueSplitData?.deliveryOffice?.name,
                    )}
                  />
                </MuiGrid>
              </MuiGrid>
            </>
          )}
          <MuiGrid
            container
            columnSpacing={2}
            rowSpacing={isMobile ? 2 : 5}
            className="manage-revenue-split-salesSplit"
          >
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getLabel("salesSplit")}
                replaceEmptyWithDash
                value={getValueOrBlank(currentRevenueSplitData?.salesSplit)}
              />
            </MuiGrid>
            <MuiGrid
              className="manage-revenue-split-programFee"
              size={gridSize}
              container
            >
              <MuiDoubleLabel
                label={programProjectFeeLabel}
                replaceEmptyWithDash={isEmpty(
                  isForProject
                    ? currentRevenueSplitData?.projectFee
                    : currentRevenueSplitData?.programFee,
                )}
                value={programProjectFeeFormattedValue}
              />
            </MuiGrid>
            <MuiGrid
              className="manage-revenue-split-minimumTransferFee"
              size={gridSize}
              container
            >
              <MuiDoubleLabel
                label={getLabel("minimumTransferFee")}
                replaceEmptyWithDash={isEmpty(
                  currentRevenueSplitData?.minimumTransferFee,
                )}
                value={getFormattedCurrency(
                  currentRevenueSplitData?.minimumTransferFee,
                )}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiBox>

        {isNotEmpty(sectionItems) && (
          <AccordionHeader
            title={
              <AccordionHeaderText
                label={getLabel("revenueSplitSectionHeader")}
              />
            }
            isExpanded={true}
            showExpandCollapseIcon={false}
          >
            <MuiBox className="revenue-split-deliverables-section">
              <MuiGrid
                container
                columnSpacing={2}
                rowSpacing={isMobile ? 2 : 5}
              >
                {sectionItems.map((item) => (
                  <MuiGrid
                    className="notes-container"
                    size={customGridSize}
                    container
                    key={item.value}
                  >
                    <MuiTripleLabel
                      label={getLabel(item.label)}
                      value={item.value}
                      subValue={item.subValue}
                      shouldShowSubValue={!isMigratedRevenueSplits}
                      isValueWithEllipsis={false}
                      replaceEmptyWithDash={
                        isEmpty(item.value) || isEmpty(item.subValue)
                      }
                    />
                  </MuiGrid>
                ))}
              </MuiGrid>
            </MuiBox>
          </AccordionHeader>
        )}
        <MuiBox className="separator-space" />
        <MuiBox className="manage-revenue-split-modal-box-footer">
          <MuiTypography className="total-amount-label">
            {getLabel("revenueSplitSectionTotalAmount")}
          </MuiTypography>
          <MuiTypography variant="h3SemiBold" component="span">
            {getFormattedCurrency(
              currentRevenueSplitData?.revenueSplitBreakdownResponse
                ?.splitTotals?.total?.amount,
            )}
          </MuiTypography>
        </MuiBox>

        <ManageRevenueSplitTable
          filteredResults={revenueSplitAmountData}
          currencyLocale={currencyLocale}
          invoiceId={invoiceId}
          onUpdateRevenueSplit={handleFetchRevenueSplitData}
          isViewOnly={isViewOnly}
          invoiceStatus={invoiceStatus}
          isInvoiceReversed={isInvoiceReversed}
          isForProject={isForProject}
        />
      </ManageRevenueSplitWrapper>
    </MuiModal>
  );
};
